import { BigNumber } from 'bignumber.js';
import {
  convertToBigNumber,
  unformatDecimal,
} from '@paradigm/utils/formatDecimal';
import { Maybe, Nullable } from '@paradigm/utils/types';
import { Bbo } from '#/unified-rfqs/entities/drfq/bbo';
import { DrfqLeg } from '#/unified-rfqs/entities/drfq/domain';
import { GrfqBbo } from '#/unified-rfqs/entities/grfq/bbo';
import { GrfqLeg } from '#/unified-rfqs/entities/grfq/domain';
import { findProduct, isOptionProduct } from '#/products/domain';
import { Rfq } from '#/unified-rfqs/entities/rfq';

interface BboOffsetArgs {
  readonly bidPrice: Maybe<string>;
  readonly askPrice: Maybe<string>;
  readonly bestBidQty: Maybe<string>;
  readonly bestBidPrice: Maybe<string>;
  readonly bestAskQty: Maybe<string>;
  readonly bestAskPrice: Maybe<string>;
  readonly productCode: Maybe<string>;
  readonly formatAsk?: (price: Maybe<string>) => string;
  readonly formatBid?: (price: Maybe<string>) => string;
}

export function getBboOffset({
  bidPrice,
  askPrice,
  bestBidQty,
  bestBidPrice,
  bestAskQty,
  bestAskPrice,
  formatBid,
  formatAsk,
}: BboOffsetArgs) {
  const screenBidPrice = getScreenPrice(bestBidQty, bestBidPrice);
  const screenAskPrice = getScreenPrice(bestAskQty, bestAskPrice);

  const bidDiff = getDiffValue(bidPrice, screenBidPrice);
  const askDiff = getDiffValue(askPrice, screenAskPrice);
  const offsetBid = formatValue(bidDiff, formatBid);
  const offsetAsk = formatValue(askDiff, formatAsk);
  const isBetterThanScreenBid = bidDiff != null && bidDiff.gt(0);
  const isBetterThanScreenAsk = askDiff != null && askDiff.lt(0);

  return {
    offsetBid,
    offsetAsk,
    isBetterThanScreenBid,
    isBetterThanScreenAsk,
  };
}

function getDiffValue(
  price: string | null | undefined,
  bboPrice: string | null | undefined,
) {
  if (price == null || bboPrice == null) return null;
  const diff = BigNumber(price).minus(bboPrice);
  return diff;
}

function formatValue(
  value: Nullable<BigNumber>,
  formatter?: (price: Maybe<string>) => string,
) {
  if (value == null) return null;
  const formatted =
    formatter != null ? formatter(value.toString()) : value.toString();
  if (value.isGreaterThan(0)) {
    return `+${formatted}`;
  }

  return formatted;
}

export function parseIv(value: string | null | undefined) {
  if (value == null) return null;
  return convertToBigNumber(value).isZero() ? null : value;
}

export function getScreenPrice(
  quantity: Maybe<string>,
  price: Maybe<string>,
): Maybe<string> {
  const bestQtyNumber = new BigNumber(quantity ?? 0);
  const isZeroQty = bestQtyNumber.isZero();
  const bestPrice = isZeroQty ? null : price ?? null;
  return bestPrice;
}

/**
 * Mirror of the backend `trading.utils.hedge.hedge_leg_net_value`
 * (see api/on-demand-markets/unified_rfq/CLAUDE.md). Value of a hedge
 * leg in the strategy's quote currency: linear -> markingPrice * ratio;
 * inverse (coin-margined) -> -(ratio * price) / markingPrice.
 */
export function hedgeLegNetValue(
  isInverse: boolean,
  ratio: BigNumber,
  price: BigNumber,
  markingPrice: BigNumber,
): BigNumber {
  return isInverse
    ? ratio.times(price).div(markingPrice).negated()
    : markingPrice.times(ratio);
}

/** Structural leg shape both GrfqLeg and DrfqLeg satisfy. */
interface HedgeableLeg {
  readonly product_code: string;
  readonly ratio: string;
  readonly side: 'BUY' | 'SELL';
  readonly price?: string | null;
  readonly isHedge: boolean;
}

function signedHedgeRatio(leg: HedgeableLeg): BigNumber {
  const ratio = convertToBigNumber(leg.ratio);
  return leg.side === 'BUY' ? ratio : ratio.negated();
}

/**
 * Hedge contribution to a structure price on the FILL basis: each hedge
 * leg valued at its own fixed price (what it trades at), the same basis
 * the backend uses for `net_traded_price` and the live best bid/ask.
 * Added to the option premium, this gives the net structure price the
 * maker sees; subtract it to recover the premium.
 */
export function hedgeFillValue(legs: readonly HedgeableLeg[]): BigNumber {
  return legs.reduce((total, leg) => {
    if (!leg.isHedge || leg.price == null) return total;
    const product = findProduct(leg.product_code);
    // Unknown hedge product: skip the leg rather than throw on a render
    // path, matching the backend (_hedge_currency_config skips it).
    if (product == null) return total;
    const price = convertToBigNumber(leg.price);
    const isInverse = product.marginType === 'INVERSE';
    return total.plus(
      hedgeLegNetValue(isInverse, signedHedgeRatio(leg), price, price),
    );
  }, BigNumber(0));
}

/**
 * The maker quotes the net structure price (consistent with the net
 * mark/BBO). Recover the option premium the pricing endpoints expect by
 * removing the hedge's fill-basis value. A no-op when there is no hedge.
 */
export function netPriceToPremium(
  netPrice: string,
  legs: readonly HedgeableLeg[],
): string {
  const hedgeFill = hedgeFillValue(legs);
  if (hedgeFill.isZero() || netPrice.trim() === '') return netPrice;
  // toFixed, not toString: no exponential notation on the submit path.
  return convertToBigNumber(unformatDecimal(netPrice))
    .minus(hedgeFill)
    .toFixed();
}

export function getBboLegs(bbo: GrfqBbo | Bbo | null) {
  if (bbo == null) return null;
  return 'legs' in bbo ? bbo.legs : bbo.leg_prices;
}

export function getScreenQty(quantity: Maybe<string>) {
  const bestQtyNumber = new BigNumber(quantity ?? 0);
  const isZeroQty = bestQtyNumber.isZero();

  return isZeroQty ? null : bestQtyNumber.toString();
}

/**
 *
 * Iv values should only be displayed accross the app if:
 * - rfq has a single option leg
 *
 * This check is used by bbo view so the formatted value displays
 * the iv number or an empty placeholder
 *
 * @param rfq
 * @returns {boolean}
 */
export function shouldShowIv(rfq: Nullable<Rfq>): boolean {
  if (rfq == null) {
    return false;
  }
  const { legs, product_codes } = rfq;
  const regularLegs = (legs as (GrfqLeg | DrfqLeg)[]).filter(
    (leg) => !leg.isHedge,
  );
  const isSingleLeg = regularLegs.length === 1;
  const haveOptions = product_codes.some(isOptionProduct);

  return isSingleLeg && haveOptions;
}
